import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./KeywordTrendsAnimationPage.css";
import Header from "./Header";
import KeywordBubbleChart from "./KeywordBubbleChart";
import AnimationDatasetTable from "./AnimationDatasetTable";
import { useClient } from "../contexts/ClientContext";
import { clusterApi } from "../services/api";
import { KeywordWithMetadata, TrendData } from "../types";
import {
  createApiSnapshotProvider,
  Snapshot,
} from "../services/animationSnapshotProvider";
import { parseServerDate } from "../utils/formatters";
import Tooltip from "@mui/material/Tooltip";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ReplayIcon from "@mui/icons-material/Replay";
import RepeatIcon from "@mui/icons-material/Repeat";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Footer from "./Footer";

type AnimationRouteState = {
  selectedAnchor?: string;
  initialData?: KeywordWithMetadata[];
  anchorKeywords?: string[];
  /** ISO date string of when the cluster was created — used as the DatePicker minDate. */
  clusterCreatedAt?: string;
  locationCode?: number;
  languageCode?: string;
};

type AxisRange = {
  x: { min: number; max: number };
  y: { min: number; max: number };
};

type DateRangeValue = [Date | null, Date | null];

/**
 * Formats an ISO date string into a human-readable label (e.g. "Mon, Mar 18, 2024").
 */
const formatDateForLabel = (dateString: string): string => {
  return new Date(`${dateString}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Maps a raw API response item to the KeywordWithMetadata shape used throughout
 * the app, and flags the entry as anchor when its keyword matches anchorKeyword.
 */
const toKeywordWithMetadata = (
  anchorKeyword: string,
  responseData: {
    keyword: string;
    volume: number;
    difficulty: number;
    ranking: number;
    trend: TrendData;
    relevance_score?: number;
    semantic_similarity?: number;
    length_score?: number;
    intent_score?: number;
    keyword_overlap?: number;
    openai_similarity?: number;
    ranking_url?: string;
  }[],
): KeywordWithMetadata[] => {
  return responseData.map((item) => ({
    keyword: item.keyword,
    volume: item.volume,
    difficulty: item.difficulty,
    ranking: item.ranking,
    trend: item.trend,
    isAnchor: item.keyword === anchorKeyword,
    parentAnchor: anchorKeyword,
    relevance_score: item.relevance_score,
    semantic_similarity: item.semantic_similarity,
    length_score: item.length_score,
    intent_score: item.intent_score,
    keyword_overlap: item.keyword_overlap,
    openai_similarity: item.openai_similarity,
    ranking_url: item.ranking_url,
  }));
};

/**
 * Computes a fixed x/y axis range covering all snapshots in the timeline,
 * with a small padding buffer on each side.
 *
 * Passing this fixed range to KeywordBubbleChart keeps the axes stable across
 * all frames so bubbles visibly move rather than the axes rescaling each step.
 */
const buildAxisRangeFromSnapshots = (
  timeline: Snapshot[],
): AxisRange | null => {
  if (!timeline.length) return null;

  const allKeywords = timeline.flatMap((snapshot) => snapshot.data);
  if (!allKeywords.length) return null;

  const xValues = allKeywords.map((item) => item.difficulty);
  const yValues = allKeywords.map((item) => item.volume);

  // Include each frame's center so the diamond stays in view across the timeline.
  timeline.forEach((snapshot) => {
    const c = snapshot.center;
    if (c && c.difficulty != null && c.volume != null) {
      xValues.push(c.difficulty);
      yValues.push(c.volume);
    }
  });

  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);

  const xRange = Math.max(1, maxX - minX);
  const yRange = Math.max(1, maxY - minY);

  const xPadding = xRange * 0.08;
  const yPadding = yRange * 0.1;

  return {
    x: {
      min: Math.max(0, minX - xPadding),
      max: Math.min(100, maxX + xPadding),
    },
    y: {
      min: Math.max(0, minY - yPadding),
      max: maxY + yPadding,
    },
  };
};

const KeywordTrendsAnimationPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentClient } = useClient();
  const location = useLocation();
  const routeState = (location.state as AnimationRouteState | null) ?? null;

  // --- Anchor keyword & base dataset (seeded from route state or fetched fresh) ---
  const [selectedAnchor, setSelectedAnchor] = useState<string>(
    routeState?.selectedAnchor ?? "",
  );
  const [locationCode, setLocationCode] = useState<number>(
    routeState?.locationCode ?? 2840,
  );
  const [languageCode, setLanguageCode] = useState<string>(
    routeState?.languageCode ?? "en",
  );
  const [baseData, setBaseData] = useState<KeywordWithMetadata[]>(
    routeState?.initialData ?? [],
  );
  const [isLoadingBaseData, setIsLoadingBaseData] = useState<boolean>(false);

  // Cluster creation date — earliest selectable date for the date range picker.
  // Passed via route state from the dashboard; falls back to the anchor list fetch.
  const clusterCreatedAt = routeState?.clusterCreatedAt
    ? parseServerDate(routeState.clusterCreatedAt)
    : null;

  // --- Date range chosen by the user before clicking "Get Data" ---
  const [dateRange, setDateRange] = useState<DateRangeValue>([null, null]);

  // Loading state for the "Get Data" API call.
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
  // Error message shown if the history fetch fails.
  const [historyError, setHistoryError] = useState<string | null>(null);

  // --- Generated timeline & playback controls ---
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [currentSnapshotIndex, setCurrentSnapshotIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isLooping, setIsLooping] = useState<boolean>(false);
  const [bubbleAnimationDurationMs, setBubbleAnimationDurationMs] =
    useState<number>(1000);
  const frameDurationMs =
    bubbleAnimationDurationMs +
    (bubbleAnimationDurationMs <= 700
      ? 100
      : bubbleAnimationDurationMs <= 1500
        ? 70
        : 10);
  const [showLabels, setShowLabels] = useState<boolean>(false);
  const [snapshotAxisRange, setSnapshotAxisRange] = useState<AxisRange | null>(
    null,
  );
  const [hasGeneratedRange, setHasGeneratedRange] = useState<boolean>(false); // true once "Get Data" has been clicked

  // If no anchor was passed via route state, fetch the list and default to the first one.
  useEffect(() => {
    let isMounted = true;

    const fetchDefaultAnchor = async () => {
      if (selectedAnchor) return;

      try {
        const anchors = await clusterApi.getAnchorKeywords(currentClient);
        if (!isMounted) return;

        const sorted = [...anchors].sort((a, b) =>
          a.anchor_keyword
            .toLowerCase()
            .localeCompare(b.anchor_keyword.toLowerCase()),
        );

        if (sorted.length > 0) {
          setSelectedAnchor(sorted[0].anchor_keyword);
          setLocationCode(sorted[0].location_code ?? 2840);
          setLanguageCode(sorted[0].language_code ?? "en");
        }
      } catch (error) {
        console.error(
          "Failed to load anchor keywords for animation page:",
          error,
        );
      }
    };

    fetchDefaultAnchor();

    return () => {
      isMounted = false;
    };
  }, [currentClient, selectedAnchor]);

  // Whenever the selected anchor changes, load its cluster data.
  // If the data was already forwarded through route state for this anchor, skip the API call.
  useEffect(() => {
    if (!selectedAnchor) return;

    const usingRouteDataForAnchor =
      routeState?.selectedAnchor === selectedAnchor &&
      Array.isArray(routeState?.initialData) &&
      routeState.initialData.length > 0;

    if (usingRouteDataForAnchor) {
      setBaseData(routeState!.initialData!);
      return;
    }

    let isMounted = true;

    const fetchClusterData = async () => {
      try {
        setIsLoadingBaseData(true);
        const response = await clusterApi.getClusterData(currentClient, {
          anchor_keyword: selectedAnchor,
          location_code: locationCode,
          language_code: languageCode,
        });

        if (!isMounted) return;
        setBaseData(
          toKeywordWithMetadata(
            selectedAnchor,
            response.visualization_data || [],
          ),
        );
      } catch (error) {
        console.error("Failed to load cluster data for animation page:", error);
      } finally {
        if (isMounted) {
          setIsLoadingBaseData(false);
        }
      }
    };

    fetchClusterData();

    return () => {
      isMounted = false;
    };
  }, [currentClient, routeState, selectedAnchor]);

  // Animation requires at least 2 real snapshots.
  const canAnimate = snapshots.length > 1;

  // The dataset rendered in the chart: the active snapshot, or base data before any generation.
  const currentData = useMemo(() => {
    if (snapshots.length === 0) return baseData;
    return snapshots[currentSnapshotIndex]?.data ?? baseData;
  }, [baseData, currentSnapshotIndex, snapshots]);

  // The active frame's cluster-center diamond (null before any snapshots).
  const currentCenter = useMemo(
    () => snapshots[currentSnapshotIndex]?.center ?? null,
    [currentSnapshotIndex, snapshots],
  );

  const hasReachedEnd =
    snapshots.length > 0 && currentSnapshotIndex === snapshots.length - 1;

  useEffect(() => {
    if (!isPlaying || !canAnimate) return;

    if (currentSnapshotIndex >= snapshots.length - 1) {
      if (isLooping) {
        // At the last frame in loop mode — hold for one frame then restart.
        const timer = setTimeout(
          () => setCurrentSnapshotIndex(0),
          frameDurationMs,
        );
        return () => clearTimeout(timer);
      } else {
        setIsPlaying(false);
        return;
      }
    }

    // Frame duration controls how often we advance to the next snapshot.
    const timer = setTimeout(() => {
      setCurrentSnapshotIndex((prev) =>
        Math.min(prev + 1, snapshots.length - 1),
      );
    }, frameDurationMs);

    return () => clearTimeout(timer);
  }, [
    canAnimate,
    currentSnapshotIndex,
    frameDurationMs,
    isLooping,
    isPlaying,
    snapshots.length,
  ]);

  const handleGenerate = async () => {
    const [startDate, endDate] = dateRange;
    if (!startDate || !endDate) return;

    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    if (start > end) {
      setSnapshots([]);
      setSnapshotAxisRange(null);
      setHasGeneratedRange(true);
      setIsPlaying(false);
      return;
    }

    setIsLoadingHistory(true);
    setHistoryError(null);

    try {
      // Use the real API provider — fetches historical data from the DB.
      // createApiSnapshotProvider is instantiated here (not at module level)
      // so it always captures the current client and anchor.
      const provider = createApiSnapshotProvider(
        currentClient,
        selectedAnchor,
        languageCode,
        locationCode,
      );
      const { snapshots: timeline } = await provider.generateSnapshots({
        baseData,
        startDate: start,
        endDate: end,
      });

      setSnapshots(timeline);
      setSnapshotAxisRange(buildAxisRangeFromSnapshots(timeline));
      setCurrentSnapshotIndex(0);
      setHasGeneratedRange(true);
      setIsPlaying(false); // User must press Play to start the animation.
    } catch (err) {
      console.error("Failed to load keyword history:", err);
      setHistoryError("Failed to load history data. Please try again.");
      setSnapshots([]);
      setSnapshotAxisRange(null);
      setHasGeneratedRange(true);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const replayAnimation = () => {
    if (!canAnimate) return;
    setCurrentSnapshotIndex(0);
    setIsPlaying(true);
  };

  // Ref pointing to the currently active timeline item so we can scroll it into view.
  const activeTimelineItemRef = useRef<HTMLLIElement>(null);
  // Ref to the scrollable <ul> so we can listen for user-initiated scroll events.
  const timelineListRef = useRef<HTMLUListElement>(null);
  // Flag set to true while a programmatic scrollIntoView is in progress,
  // so the scroll event listener can distinguish it from a user scroll.
  const isProgrammaticScrollRef = useRef<boolean>(false);

  const [autoScroll, setAutoScroll] = useState<boolean>(true);

  // Auto-scroll the timeline list to keep the active date visible during playback.
  useEffect(() => {
    if (!autoScroll) return;

    isProgrammaticScrollRef.current = true;
    activeTimelineItemRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
    // Clear the flag after smooth scrolling finishes (~500 ms max).
    const timer = setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 500);
    return () => clearTimeout(timer);
  }, [autoScroll, currentSnapshotIndex]);

  // Detect manual scrolling and turn off autoscroll.
  useEffect(() => {
    const list = timelineListRef.current;
    if (!list) return;

    const handleScroll = () => {
      if (!isProgrammaticScrollRef.current) {
        setAutoScroll(false);
      }
    };

    list.addEventListener("scroll", handleScroll, { passive: true });
    return () => list.removeEventListener("scroll", handleScroll);
    // snapshots.length is a dependency so the effect re-runs once the <ul>
    // is conditionally rendered (it doesn't exist on the first mount).
  }, [snapshots.length]);

  const handleSelectSnapshot = (index: number) => {
    setCurrentSnapshotIndex(index);
    setIsPlaying(false);
  };

  return (
    <div className="App">
      <Header />

      <main className="App-main">
        <div className="dashboard-layout">
          <div className="sidebar">
            <div className="flex flex-col gap-y-6">
              <div
                className="flex items-center gap-1.5 cursor-pointer text-my-purple font-semibold hover:underline -mb-4"
                onClick={() => navigate(`/${currentClient}`)}
              >
                <ArrowBackIcon style={{ fontSize: 20 }} />
                <span>Go back to dashboard</span>
              </div>
              <h3
                style={{
                  fontFamily: "Roundo",
                }}
                className="font-bold text-2xl"
              >
                Keyword Trends Animation
              </h3>

              <div className="">
                <label className="label mb-1.5 block">Anchor Keyword:</label>
                <div
                  className="px-4 py-2 relative font-semibold text-lg"
                  style={{
                    border: "3px solid transparent",
                    backgroundImage:
                      "linear-gradient(#F3F4FF, #F3F4FF), linear-gradient(92.69deg, #8642C7 4.57%, #8A96FF 100%)",
                    borderRadius: "30px",
                    backgroundOrigin: "border-box",
                    backgroundClip: "padding-box, border-box",
                  }}
                >
                  {selectedAnchor ||
                    "No anchor selected. Go back to dashboard."}
                </div>
              </div>

              <div className="flex flex-col gap-y-1.5 overflow-hidden">
                <label className="label">Select Date Range:</label>
                {/* minDate restricts selection to on/after the cluster creation date;
                    maxDate prevents selecting future dates with no data yet. */}
                <DatePicker
                  selectsRange
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  onChange={(newRange: DateRangeValue) =>
                    setDateRange(newRange)
                  }
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select start and end date"
                  isClearable
                  minDate={clusterCreatedAt ?? undefined}
                  maxDate={new Date()}
                  className="date-range-picker-input"
                />
              </div>

              <div className="flex justify-end">
                <button
                  className="bg-my-purple font-semibold text-white rounded-full px-[18px] py-[12px]"
                  onClick={handleGenerate}
                  disabled={
                    isLoadingBaseData ||
                    isLoadingHistory ||
                    !selectedAnchor ||
                    !dateRange[0] ||
                    !dateRange[1]
                  }
                >
                  {isLoadingHistory ? "Loading..." : "Get Data"}
                </button>
                {historyError && (
                  <div className="alert warning">{historyError}</div>
                )}
              </div>

              {/* Show animation settings only after Get Data has been clicked */}
              {hasGeneratedRange && canAnimate && (
                <div>
                  <div className="label mb-1.5">Visualization Controls</div>

                  <div className="bg-white py-3 px-4 rounded-xl flex flex-col gap-y-2">
                    <div className="playback-group">
                      <label
                        htmlFor="bubble-animation-duration"
                        className="text-sm"
                      >
                        Bubble movement duration:
                        <span className="font-semibold text-base">
                          {" "}
                          {(bubbleAnimationDurationMs / 1000).toFixed(1)}s
                        </span>
                      </label>
                      {/* Visual transition duration inside the chart for each frame change. */}
                      <input
                        id="bubble-animation-duration"
                        type="range"
                        min={500}
                        max={4000}
                        step={100}
                        value={bubbleAnimationDurationMs}
                        onChange={(e) =>
                          setBubbleAnimationDurationMs(Number(e.target.value))
                        }
                      />
                    </div>

                    <label className="checkbox-container flex-row-reverse justify-end gap-2">
                      <input
                        id="show-labels-checkbox"
                        type="checkbox"
                        checked={showLabels}
                        onChange={(e) => setShowLabels(e.target.checked)}
                        className="checkbox"
                      />
                      <span className="checkmark"></span>
                      <span className="text-sm font-medium">
                        Show Keyword Labels
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {hasGeneratedRange && canAnimate && (
                <div className="playback-icon-controls">
                  <Tooltip title="Restart" placement="top">
                    <span>
                      <button
                        className="playback-icon-btn"
                        onClick={replayAnimation}
                        disabled={!canAnimate}
                      >
                        <ReplayIcon />
                      </button>
                    </span>
                  </Tooltip>

                  <Tooltip title={isPlaying ? "Pause" : "Play"} placement="top">
                    <span>
                      <button
                        className={`playback-icon-btn${isPlaying ? " playing" : ""}`}
                        onClick={() => setIsPlaying((prev) => !prev)}
                        disabled={!canAnimate || (hasReachedEnd && !isLooping)}
                      >
                        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                      </button>
                    </span>
                  </Tooltip>

                  <Tooltip
                    title={isLooping ? "Loop: On" : "Loop: Off"}
                    placement="top"
                  >
                    <span>
                      <button
                        className={`playback-icon-btn${isLooping ? " active" : ""}`}
                        onClick={() => setIsLooping((prev) => !prev)}
                        disabled={!canAnimate}
                      >
                        <RepeatIcon />
                      </button>
                    </span>
                  </Tooltip>
                </div>
              )}

              {hasGeneratedRange && !canAnimate && (
                <div className="text-sm bg-red-200 text-red-600 font-medium px-4 py-2 rounded-xl">
                  Only one weekly snapshot is available in this range, so
                  animation is disabled and a static graph is shown.
                </div>
              )}

              {hasGeneratedRange && (
                <div className="bg-white rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">Update Dates</h3>
                    <label className="flex items-center gap-2 text-sm checkbox-container">
                      <input
                        type="checkbox"
                        checked={autoScroll}
                        onChange={(e) => setAutoScroll(e.target.checked)}
                        className="checkbox"
                      />
                      <span className="checkmark !h-4 !w-4"></span>
                      Autoscroll
                    </label>
                  </div>
                  {snapshots.length === 0 ? (
                    <p className="timeline-empty">
                      No updates found for this date range.
                    </p>
                  ) : (
                    <ul className="timeline-list" ref={timelineListRef}>
                      {snapshots.map((snapshot, index) => (
                        <li
                          key={snapshot.date}
                          ref={
                            index === currentSnapshotIndex
                              ? activeTimelineItemRef
                              : null
                          }
                          className={`timeline-item ${
                            index === currentSnapshotIndex ? "active" : ""
                          }`}
                          onClick={() => handleSelectSnapshot(index)}
                        >
                          <button
                            type="button"
                            className="timeline-item-button"
                          >
                            <span className="timeline-index">{index + 1}</span>
                            <span>{formatDateForLabel(snapshot.date)}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="flex justify-between items-end">
              <div className="text-sm bg-black text-white w-fit px-2 py-1 rounded-t-sm">
                Current snapshot:{" "}
                {snapshots.length > 0
                  ? formatDateForLabel(snapshots[currentSnapshotIndex].date)
                  : "Base data"}
              </div>

              {canAnimate && (
                <div className="bg-my-purple text-white font-medium text-sm px-2 py-1 rounded-t-sm">
                  {isPlaying && isLooping
                    ? "\uD83D\uDD01 Playing (looping)"
                    : isPlaying
                      ? "\u25B6\uFE0F Playing"
                      : hasReachedEnd
                        ? "\u2713 Completed"
                        : "\u23F8\uFE0F Paused"}
                </div>
              )}
            </div>

            <div className="chart-container !rounded-t-none">
              {isLoadingBaseData ? (
                <div className="loading">Loading keyword data...</div>
              ) : !hasGeneratedRange ? (
                <div className="loading">
                  Select a date range and click{" "}
                  <strong style={{ marginLeft: 6 }}>Get Data</strong>.
                </div>
              ) : snapshots.length === 0 ? (
                <div className="loading">
                  No data generated for the selected range.
                </div>
              ) : (
                <KeywordBubbleChart
                  data={currentData}
                  center={currentCenter}
                  useDynamicAxes={false}
                  showLabels={showLabels}
                  colorMode="trend"
                  trendPeriod="life"
                  animationDuration={bubbleAnimationDurationMs}
                  fixedAxisRange={snapshotAxisRange ?? undefined}
                />
              )}
            </div>

            <AnimationDatasetTable
              snapshots={snapshots}
              anchorKeyword={selectedAnchor}
              exportDateRange={dateRange}
            />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default KeywordTrendsAnimationPage;
